import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import omniLogo from './assets/omninew.jpg';
import { isEncryptedToken } from './crypto';
import Admin from './components/Admin';
import Trading from './components/Trading';
import Videos from './components/Videos';
import Docs from './components/Docs';
import MemeCoins from './components/MemeCoins';
import MemeCoinBots from './components/MemeCoinBots';
import MultiAssetTrading from './components/MultiAssetTrading';
import OBotTrading from './components/OBotTrading';
import AdminOmniMapTrading from './components/AdminOmniMapTrading';
import ManualTrades from './components/ManualTrades';
import SingleAssetHLTrading from './components/SingleAssetHLTrading';
import DiscretionaryScalping from './components/DiscretionaryScalping';
import ScalpingTrading from './components/ScalpingTrading';
import FundingCarryTrading from './components/FundingCarryTrading';
import FundingArbitrageTrading from './components/FundingArbitrageTrading';
import TelegramMessaging from './components/TelegramMessaging';
import MailboxPassword from './components/MailboxPassword';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

function RegistrationPage({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [discordId, setDiscordId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('register');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE}/auth/register`, { email, discordId });
      if (response.data?.devVerificationCode) {
        setVerificationCode(response.data.devVerificationCode);
      }
      setStep('verify');
    } catch (err) {
      const d = err.response?.data;
      setError(
        [d?.error, d?.details].filter(Boolean).join(' — ') || err.message || 'Registration failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const deviceName = `${navigator.platform} - ${navigator.userAgent.split(')')[0].split('(')[1] || 'Unknown'}`.substring(0, 50);
      const deviceFingerprint = `${navigator.platform}-${navigator.userAgent}`;

      const response = await axios.post(`${API_BASE}/auth/verify-registration`, {
        email,
        code: verificationCode,
        deviceName,
        deviceFingerprint
      });

      const tokenFile = response.data.tokenFile;
      const blob = new Blob([tokenFile], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'omni-secure-pass';
      a.click();

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <img 
           src={omniLogo} 
           alt="Omni Trading"            
          />
        </div>
        <h1>Welcome to Omni Trading</h1>
        <p className="subtitle">Create your account to access our multi-blockchain trading platform</p>

        {error && <div className="error-message">{error}</div>}

        {step === 'register' ? (
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label>Email Address</label>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="your@email.com" 
                required 
                disabled={loading} 
              />
            </div>
            <div className="form-group">
              <label>Discord ID</label>
              <input 
                type="text" 
                value={discordId} 
                onChange={(e) => setDiscordId(e.target.value)} 
                placeholder="username#1234" 
                required 
                disabled={loading} 
              />
              <small className="help-text">We'll link your Discord account for support and updates</small>
            </div>
            <button type="submit" disabled={loading} className="primary-btn">
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify}>
            <div className="form-group">
              <label>Email Verification</label>
              <p className="help-text">We sent a 6-digit code to <strong>{email}</strong></p>
              <input 
                type="text" 
                value={verificationCode} 
                onChange={(e) => setVerificationCode(e.target.value)} 
                placeholder="Enter 6-digit code" 
                maxLength="6" 
                required 
                autoFocus 
              />
            </div>
            <button type="submit" disabled={loading} className="primary-btn">
              {loading ? 'Verifying...' : 'Verify & Download Secure Pass'}
            </button>
            <button type="button" onClick={() => setStep('register')} className="secondary-btn">
              Back to Registration
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function RecoveryPage({ onBack, onSuccess }) {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('request');
  const [devices, setDevices] = useState([]);
  const [replaceMode, setReplaceMode] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRecoveryRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE}/auth/recover-request`, { email });
      if (response.data?.devVerificationCode) {
        setVerificationCode(response.data.devVerificationCode);
      }
      setStep('verify');
    } catch (err) {
      const d = err.response?.data;
      setError(
        [d?.error, d?.details].filter(Boolean).join(' — ') || err.message || 'Recovery request failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const deviceName = replaceMode ? newDeviceName : '';
      const deviceFingerprint = `${navigator.platform}-${navigator.userAgent}`;

      const response = await axios.post(`${API_BASE}/auth/recover-verify`, {
        email,
        code: verificationCode,
        deviceName,
        deviceFingerprint,
        replaceToken: replaceMode
      });

      if (replaceMode) {
        const tokenFile = response.data.tokenFile;
        const blob = new Blob([tokenFile], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'omni-secure-pass';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        alert('New secure pass created! Check your email and the downloaded file.');
        onSuccess();
      } else {
        setDevices(response.data.devices);
        setStep('select-device');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDevice = async (deviceTokenId) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE}/auth/recover-download`, {
        email,
        deviceTokenId
      });

      const tokenFile = response.data.tokenFile;
      const blob = new Blob([tokenFile], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'omni-secure-pass';
      a.click();

      alert('Secure pass recovered! Check your email and the downloaded file.');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Download failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <img src={omniLogo} alt="Omni Trading" />
        </div>
        <h1>🔑 Recover Secure Pass</h1>
        <p className="subtitle">Recover or replace your lost secure pass file</p>

        {error && <div className="error-message">{error}</div>}

        {step === 'request' && (
          <form onSubmit={handleRecoveryRequest}>
            <div className="form-group">
              <label>Email Address</label>
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="your@email.com" 
                required 
                disabled={loading} 
              />
              <small className="help-text">We'll send a verification code to this email</small>
            </div>

            <div className="recovery-options">
              <label className="radio-option">
                <input 
                  type="radio" 
                  checked={!replaceMode} 
                  onChange={() => setReplaceMode(false)} 
                />
                <div>
                  <strong>Recover Existing Device</strong>
                  <p>Re-download secure pass for a device you already registered</p>
                </div>
              </label>

              <label className="radio-option">
                <input 
                  type="radio" 
                  checked={replaceMode} 
                  onChange={() => setReplaceMode(true)} 
                />
                <div>
                  <strong>Replace Lost Token</strong>
                  <p>Create a new secure pass for this device (old one will stop working)</p>
                </div>
              </label>
            </div>

            {replaceMode && (
              <div className="form-group">
                <label>New Device Name</label>
                <input 
                  type="text" 
                  value={newDeviceName} 
                  onChange={(e) => setNewDeviceName(e.target.value)} 
                  placeholder="e.g., My Laptop, Work PC" 
                  required 
                />
              </div>
            )}

            <button type="submit" disabled={loading} className="primary-btn">
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
            <button type="button" onClick={onBack} className="secondary-btn">
              Back to Login
            </button>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerifyCode}>
            <div className="form-group">
              <label>Verification Code</label>
              <p className="help-text">Check your email at <strong>{email}</strong></p>
              <input 
                type="text" 
                value={verificationCode} 
                onChange={(e) => setVerificationCode(e.target.value)} 
                placeholder="Enter 6-digit code" 
                maxLength="6" 
                required 
                autoFocus 
              />
            </div>
            <button type="submit" disabled={loading} className="primary-btn">
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button type="button" onClick={() => setStep('request')} className="secondary-btn">
              Back
            </button>
          </form>
        )}

        {step === 'select-device' && (
          <div>
            <p className="help-text" style={{marginBottom: '20px'}}>Select the device you want to recover:</p>
            <div className="device-selection">
              {devices.map(device => (
                <div key={device.id} className="device-select-card">
                  <div>
                    <h4>{device.deviceName}</h4>
                    <p><small>Last used: {device.lastUsed ? new Date(device.lastUsed).toLocaleDateString() : 'Never'}</small></p>
                    <p><small>Created: {new Date(device.createdAt).toLocaleDateString()}</small></p>
                  </div>
                  <button 
                    onClick={() => handleDownloadDevice(device.id)} 
                    className="primary-btn"
                    disabled={loading}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={onBack} className="secondary-btn" style={{marginTop: '20px'}}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginPage({ onLoginSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [showMailboxPassword, setShowMailboxPassword] = useState(false);
  const navigate = useNavigate();

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      const text = await file.text();
      const tokenData = JSON.parse(text);
      const deviceFingerprint = `${navigator.platform}-${navigator.userAgent}`;

      if (isEncryptedToken(tokenData)) {
        console.log('🔐 Encrypted secure pass detected, authenticating...');
      } else {
        console.log('📝 Legacy token detected');
      }

      const response = await axios.post(`${API_BASE}/auth/login-token`, { 
  token: tokenData,
  deviceFingerprint
});

console.log('🔍 Server response:', response.data);
console.log('🔍 User data:', response.data.user);

localStorage.setItem('sessionToken', response.data.sessionToken);
localStorage.setItem('user', JSON.stringify(response.data.user));
onLoginSuccess(response.data.sessionToken, response.data.user);
navigate('/dashboard');

} catch (err) {
  console.error('Login error:', err);
  setError(err.message || err.response?.data?.error || 'Invalid secure pass file. Please check your file and try again.');
} finally {
  setLoading(false);
}

  };

  if (showRegister) {
    return <RegistrationPage onSuccess={() => { 
      alert('Registration successful! Your encrypted secure pass has been downloaded and emailed to you. Please upload it below to login.'); 
      setShowRegister(false); 
    }} />;
  }

  if (showRecovery) {
    return <RecoveryPage 
      onBack={() => setShowRecovery(false)} 
      onSuccess={() => setShowRecovery(false)} 
    />;
  }

  if (showMailboxPassword) {
    return (
      <MailboxPassword
        onBack={() => setShowMailboxPassword(false)}
      />
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <img src={omniLogo} alt="Omni Trading" />
        </div>
        <h1>Welcome Back</h1>
        <p className="subtitle">Sign in to access your trading dashboard</p>

        {error && <div className="error-message">{error}</div>}

        <div className="login-section">
          <div className="form-group">
            <label>Upload Your Secure Pass</label>
            <div className="file-upload-wrapper">
              <input 
                type="file" 
                accept="*" 
                onChange={handleFileUpload} 
                className="file-input" 
                id="token-upload"
                disabled={loading}
              />
              <label htmlFor="token-upload" className="file-upload-label">
                <span className="file-icon">🔐</span>
                <span>{loading ? 'Authenticating...' : 'Choose Secure Pass File'}</span>
              </label>
            </div>
            <small className="help-text">File: omni-secure-pass (device-specific, encrypted)</small>
          </div>

          <button onClick={() => setShowRecovery(true)} className="link-btn">
            🔑 Lost Your Secure Pass?
          </button>

          <button onClick={() => setShowMailboxPassword(true)} className="link-btn">
            📧 Reset @omniscalping.com mailbox password
          </button>

          <div className="divider">
            <span>OR</span>
          </div>

          <button onClick={() => setShowRegister(true)} className="secondary-btn">
            Create New Account
          </button>
        </div>

        <div className="auth-footer">
          <p>🔒 Secure multi-blockchain trading platform</p>
          <div className="features-list">
            <span>✓ Device-Specific Encryption</span>
            <span>✓ AI-Powered Tools</span>
            <span>✓ 3-Day Free Trial</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeviceManagement({ sessionToken }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [addDeviceStep, setAddDeviceStep] = useState('request');
  const [newDeviceName, setNewDeviceName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      const response = await axios.get(`${API_BASE}/devices/list`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setDevices(response.data.devices);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeDevice = async (deviceId) => {
    if (!window.confirm('Are you sure? This will immediately block this device from accessing your account.')) return;

    try {
      await axios.post(`${API_BASE}/devices/revoke`, { deviceId }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      alert('Device revoked successfully!');
      loadDevices();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to revoke device');
    }
  };

  const handleAddDeviceRequest = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await axios.post(`${API_BASE}/devices/add-request`, { deviceName: newDeviceName }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setAddDeviceStep('verify');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send verification code');
    }
  };

  const handleAddDeviceVerify = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const deviceFingerprint = `${navigator.platform}-${navigator.userAgent}`;

      const response = await axios.post(`${API_BASE}/devices/add-verify`, {
        code: verificationCode,
        deviceName: newDeviceName,
        deviceFingerprint
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      const tokenFile = response.data.tokenFile;
      const blob = new Blob([tokenFile], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'omni-secure-pass';
      a.click();

      alert('Device added! Check your email for the secure pass file.');
      setShowAddDevice(false);
      setAddDeviceStep('request');
      setNewDeviceName('');
      setVerificationCode('');
      loadDevices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add device');
    }
  };

  if (loading) return <div className="loading">Loading devices...</div>;

  return (
    <div className="device-management">
      <div className="section-header">
        <h2>🔐 Device Management</h2>
        <button onClick={() => setShowAddDevice(true)} className="primary-btn">
          + Add New Device
        </button>
      </div>

      {showAddDevice && (
        <div className="modal-overlay" onClick={() => setShowAddDevice(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Device</h3>
            {error && <div className="error-message">{error}</div>}

            {addDeviceStep === 'request' ? (
              <form onSubmit={handleAddDeviceRequest}>
                <div className="form-group">
                  <label>Device Name</label>
                  <input 
                    type="text" 
                    value={newDeviceName} 
                    onChange={(e) => setNewDeviceName(e.target.value)} 
                    placeholder="e.g., iPhone 15, Work Laptop"
                    required 
                  />
                  <small className="help-text">Give this device a recognizable name</small>
                </div>
                <button type="submit" className="primary-btn">Send Verification Code</button>
                <button type="button" onClick={() => setShowAddDevice(false)} className="secondary-btn">
                  Cancel
                </button>
              </form>
            ) : (
              <form onSubmit={handleAddDeviceVerify}>
                <div className="form-group">
                  <label>Verification Code</label>
                  <p className="help-text">Check your email for the 6-digit code</p>
                  <input 
                    type="text" 
                    value={verificationCode} 
                    onChange={(e) => setVerificationCode(e.target.value)} 
                    placeholder="Enter 6-digit code" 
                    maxLength="6"
                    required 
                    autoFocus
                  />
                </div>
                <button type="submit" className="primary-btn">Verify & Download Secure Pass</button>
                <button type="button" onClick={() => setAddDeviceStep('request')} className="secondary-btn">
                  Back
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <div className="devices-grid">
        {devices.map(device => (
          <div key={device.id} className={`device-card ${device.status}`}>
            <div className="device-header">
              <h3>{device.deviceName}</h3>
              <span className={`status-badge ${device.status}`}>
                {device.status === 'active' ? '✅ Active' : '🚫 Revoked'}
              </span>
            </div>
            <div className="device-details">
              <p><strong>Created:</strong> {new Date(device.createdAt).toLocaleDateString()}</p>
              <p><strong>Last Used:</strong> {device.lastUsed ? new Date(device.lastUsed).toLocaleDateString() : 'Never'}</p>
              <p><strong>Expires:</strong> {new Date(device.expiresAt).toLocaleDateString()}</p>
              <p className="fingerprint"><strong>Fingerprint:</strong> {device.fingerprint}</p>
            </div>
            {device.status === 'active' && (
              <button 
                onClick={() => handleRevokeDevice(device.id)} 
                className="revoke-btn"
              >
                Revoke Device
              </button>
            )}
          </div>
        ))}
      </div>

      {devices.length === 0 && (
        <div className="empty-state">
          <p>No devices found. Add your first device to get started!</p>
        </div>
      )}
    </div>
  );
}

function MySubscriptions({ sessionToken }) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/subscriptions/my-subscriptions`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSubscriptions(response.data.subscriptions);
      setBilling(response.data.billing);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  if (subscriptions.length === 0) {
    return (
      <div className="empty-state">
        <h3>No Active Subscriptions</h3>
        <p>Browse packages below to start your 3-day free trial!</p>
      </div>
    );
  }

  return (
    <div className="my-subscriptions">
      <h2>My Active Subscriptions</h2>
      {billing && billing.discount > 0 && (
        <div className="bundle-discount">
          <h3>🎉 Bundle Discount: {billing.discount}% OFF</h3>
          <p>Saving ${billing.discountAmount.toFixed(2)}/month!</p>
        </div>
      )}
      <div className="subscriptions-list">
        {subscriptions.map(sub => (
          <div key={sub.subscriptionId} className="subscription-card">
            <div className="sub-header">
              <h3>{sub.methodology}</h3>
              <span className={`status-badge ${sub.status}`}>
                {sub.status === 'trial' ? '🎁 Free Trial' : '✅ Active'}
              </span>
            </div>
            <div className="sub-details">
              <p><strong>Package:</strong> {sub.packageCode}</p>
              <p><strong>Commission:</strong> {sub.commissionRate.code} ({sub.commissionRate.perTradePercent}%)</p>
              <p><strong>Bots:</strong> {sub.botLevel.maxBots === 999 ? 'Unlimited' : sub.botLevel.maxBots}</p>
              <p><strong>Expires:</strong> {new Date(sub.expiresAt).toLocaleDateString()}</p>
            </div>
          </div>
        ))}
      </div>
      {billing && (
        <div className="billing-summary">
          <h3>Billing Summary</h3>
          <div className="billing-row"><span>Base:</span><span>${billing.basePrice.toFixed(2)}/mo</span></div>
          {billing.discount > 0 && <div className="billing-row discount"><span>Discount ({billing.discount}%):</span><span>-${billing.discountAmount.toFixed(2)}</span></div>}
          <div className="billing-row total"><strong>Total:</strong><strong>${billing.finalMonthlyPrice.toFixed(2)}/mo</strong></div>
        </div>
      )}
    </div>
  );
}

function BrowsePackages({ sessionToken }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      const response = await axios.get(`${API_BASE}/subscriptions/packages`);
      setPackages(response.data.packages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageCode) => {
    if (!window.confirm('Start 3-day free trial?')) return;
    try {
      await axios.post(`${API_BASE}/subscriptions/purchase`, { packageCode }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      alert('Subscription activated! Check your email for details.');
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Purchase failed');
    }
  };

  const methodologies = [...new Set(packages.map(p => p.methodology))];
  const filtered = filter === 'all' ? packages : packages.filter(p => p.methodology === filter);

  if (loading) return <div className="loading">Loading packages...</div>;

  return (
    <div className="packages-section">
      <h2>📦 Subscription Packages</h2>
      <p className="subtitle">All packages include 3-day free trial</p>
      <div className="methodology-filter">
        <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
        {methodologies.map(m => <button key={m} className={filter === m ? 'active' : ''} onClick={() => setFilter(m)}>{m}</button>)}
      </div>
      <div className="packages-grid">
        {filtered.map(pkg => (
          <div key={pkg.id} className="package-card">
            <div className="package-header">
              <h3>{pkg.methodology}</h3>
              <span className="package-level">Lv {pkg.subscriptionLevel}</span>
            </div>
            <div className="package-price">
              <div className="price-main">${pkg.pricing.monthly}<small>/mo</small></div>
            </div>
            <div className="package-details">
              <div className="detail-row">
                <span>Commission:</span>
                <strong>{pkg.commissionRate.perTradePercent}%</strong>
              </div>
              
              {pkg.profitRate.profitPercent > 0 && (
                <div className="detail-row">
                  <span>Profit Share:</span>
                  <strong>{pkg.profitRate.profitPercent}%</strong>
                </div>
              )}
              
              {pkg.botLevel.maxBots > 0 && (
                <div className="detail-row">
                  <span>Bots:</span>
                  <strong>{pkg.botLevel.maxBots === 999 ? 'Unlimited' : pkg.botLevel.maxBots}</strong>
                </div>
              )}
              
              {pkg.aiPackage === 'yes' && (
                <div className="detail-row">
                  <span>✨ AI Assistant Included</span>
                </div>
              )}
              
              {pkg.customAddon && pkg.customAddon.trim() !== '' && (
                <div className="detail-row">
                  <span>Custom:</span>
                  <strong>{pkg.customAddon}</strong>
                </div>
              )}
            </div>
            <button className="primary-btn" onClick={() => handlePurchase(pkg.packageCode)}>Start Free Trial</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Dashboard wrapper component
function Dashboard({ user: propUser, sessionToken: propSessionToken }) {
  const navigate = useNavigate();
  const [sessionToken, setSessionToken] = useState(propSessionToken || localStorage.getItem('sessionToken'));
  const [user, setUser] = useState(propUser || JSON.parse(localStorage.getItem('user') || '{}'));
  const [activeTab, setActiveTab] = useState('trading');
  const [isAdmin, setIsAdmin] = useState(() => {
    try {
      return !!JSON.parse(localStorage.getItem('user') || '{}').isAdmin;
    } catch {
      return false;
    }
  });
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMailboxPassword, setShowMailboxPassword] = useState(false);

  const checkAdminStatus = async () => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if (u.isAdmin) setIsAdmin(true);

      const token = localStorage.getItem('sessionToken');
      if (!token) return;

      const response = await fetch(`${API_BASE}/admin/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIsAdmin(response.ok);
    } catch (err) {
      console.error('Admin check error:', err);
      try {
        setIsAdmin(!!JSON.parse(localStorage.getItem('user') || '{}').isAdmin);
      } catch {
        setIsAdmin(false);
      }
    }
  };

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-logo">
          <img src={omniLogo} alt="Omni Trading" />
          <h1>Omni Trading Platform</h1>
        </div>
        <div className="user-info">
          <span>{user?.email || 'User'}</span>
          <div className="profile-menu-container">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="profile-btn"
            >
              👤 Profile
            </button>
            {showProfileMenu && (
              <div className="profile-dropdown">
                <button onClick={() => { setActiveTab('subscriptions'); setShowProfileMenu(false); }}>
                  📊 My Subscriptions
                </button>
                <button onClick={() => { setActiveTab('browse'); setShowProfileMenu(false); }}>
                  📦 Browse Packages
                </button>
                <button onClick={() => { setActiveTab('devices'); setShowProfileMenu(false); }}>
                  🔐 Manage Devices
                </button>
                <button onClick={() => { setShowMailboxPassword(true); setShowProfileMenu(false); }}>
                  📧 Mailbox Password
                </button>
                <div className="dropdown-divider"></div>
                <button onClick={() => { handleLogout(); setShowProfileMenu(false); }} className="logout-dropdown-btn">
                  🚪 Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <nav className="app-nav">
        <button
          className={`nav-item ${activeTab === 'trading' ? 'active' : ''}`}
          onClick={() => setActiveTab('trading')}
        >
          Trading
        </button>
        <button
          className={`nav-item ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          Videos
        </button>
        <button
          className={`nav-item ${activeTab === 'docs' ? 'active' : ''}`}
          onClick={() => setActiveTab('docs')}
        >
          Docs
        </button>
        <button
          className={`nav-item ${activeTab === 'telegram' ? 'active' : ''}`}
          onClick={() => setActiveTab('telegram')}
        >
          Telegram
        </button>
        <button
          type="button"
          className="nav-item"
          onClick={() => navigate('/meme-bots')}
        >
          🤖 Meme bots
        </button>
        {isAdmin && (
          <button className={activeTab === 'admin' ? 'active' : ''} onClick={() => setActiveTab('admin')}>
            👑 Admin
          </button>
        )}
        {isAdmin && (
          <button
            className="commission-link-btn"
            onClick={() => window.open('https://commission.omniscalping.com', '_blank', 'noopener,noreferrer')}
          >
            💰 Commission Tracking
          </button>
        )}
      </nav>
      <main className="app-content">
        {activeTab === 'trading' && <Trading />}
        {activeTab === 'videos' && <Videos />}
        {activeTab === 'docs' && <Docs />}
        {activeTab === 'subscriptions' && <MySubscriptions sessionToken={sessionToken} />}
        {activeTab === 'browse' && <BrowsePackages sessionToken={sessionToken} />}
        {activeTab === 'devices' && <DeviceManagement sessionToken={sessionToken} />}
        {activeTab === 'admin' && isAdmin && <Admin />}
        {activeTab === 'telegram' && <TelegramMessaging isAdmin={isAdmin} />}
      </main>
      {showMailboxPassword && (
        <div className="mailbox-modal-overlay" onClick={() => setShowMailboxPassword(false)}>
          <div className="mailbox-modal-content" onClick={(e) => e.stopPropagation()}>
            <MailboxPassword embedded onBack={() => setShowMailboxPassword(false)} initialEmail="" />
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
  const token = localStorage.getItem('sessionToken');
  if (token) {
    setSessionToken(token);
    setUser(JSON.parse(localStorage.getItem('user') || '{}'));
    setAuthenticated(true);
  } else {
    setAuthenticated(false);
  }
}, []);


  const handleLoginSuccess = (token, userData) => {
  localStorage.setItem('sessionToken', token);
  localStorage.setItem('user', JSON.stringify(userData));
  setSessionToken(token);
  setUser(userData);
  setAuthenticated(true);
};


  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        {/* Login Route */}
        <Route
          path="/"
          element={
            authenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage onLoginSuccess={handleLoginSuccess} />
            )
          }
        />

        {/* Dashboard Route */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard user={user} sessionToken={sessionToken} />
            </ProtectedRoute>
          }
        />

        {/* MemeCoins Route - Protected */}
        <Route
          path="/memecoins"
          element={
            <ProtectedRoute>
              <MemeCoins
                user={user || JSON.parse(localStorage.getItem('user') || '{}')}
                sessionToken={sessionToken || localStorage.getItem('sessionToken')}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/meme-bots"
          element={
            <ProtectedRoute>
              <MemeCoinBots />
            </ProtectedRoute>
          }
        />

        <Route
          path="/multi-asset"
          element={
            <ProtectedRoute>
              <MultiAssetTrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/multi-asset/manual"
          element={
            <ProtectedRoute>
              <ManualTrades />
            </ProtectedRoute>
          }
        />

        <Route
          path="/multi-asset/obot"
          element={
            <ProtectedRoute>
              <OBotTrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/multi-asset/admin/omni"
          element={
            <ProtectedRoute>
              <AdminOmniMapTrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/scalping"
          element={
            <ProtectedRoute>
              <ScalpingTrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ovebot"
          element={
            <ProtectedRoute>
              <FundingCarryTrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/funding-carry"
          element={
            <ProtectedRoute>
              <FundingCarryTrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/funding-arbitrage"
          element={
            <ProtectedRoute>
              <FundingArbitrageTrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/single-asset-hl"
          element={
            <ProtectedRoute>
              <SingleAssetHLTrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/single-asset-hl/:symbol"
          element={
            <ProtectedRoute>
              <SingleAssetHLTrading />
            </ProtectedRoute>
          }
        />

        <Route
          path="/discretionary-scalping"
          element={
            <ProtectedRoute>
              <DiscretionaryScalping />
            </ProtectedRoute>
          }
        />

        <Route
          path="/discretionary-scalping/:symbol"
          element={
            <ProtectedRoute>
              <DiscretionaryScalping />
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
